import React, { ReactElement } from 'react';
import { Field, getIn } from 'formik';
import { Form, InputGroup } from 'react-bootstrap';
import useTranslation from 'next-translate/useTranslation';

interface IOption {
  label: string | ReactElement;
  value: string | number;
}

interface IRadioGroup {
  as?: any;
  xs?: number | 'auto' | undefined;
  sm?: number | 'auto' | undefined;
  md?: number | 'auto' | undefined;
  lg?: number | 'auto' | undefined;
  xl?: number | 'auto' | undefined;
  label?: string;
  name: string;
  options: IOption[];
  inline?: boolean;
  custom?: boolean;
  value?: any;
}

type Value = number | string | object | null | undefined;

function valueMatch(valueA: Value, valueB: Value): boolean {
  // Normalize the input values
  const normalize = (value: Value): string | number | null | undefined => {
    if (typeof value === 'object' && value !== null) {
      // Return as-is for numbers, null, or undefined
      return JSON.stringify(value);
    } else if (typeof value === 'string' && !isNaN(Number(value))) {
      return parseFloat(value); // Convert numeric strings to numbers
    }
    // Return as-is for numbers, null, or undefined
    return value;
  };

  const normalizedA = normalize(valueA);
  const normalizedB = normalize(valueB);

  return normalizedA === normalizedB;
}

export const RadioGroup = ({ label, name, options, inline, custom, value, ...bsProps }: IRadioGroup) => {
  const { t } = useTranslation('form-errors');

  return (
    <Field name={name} value={value}>
      {({ field, form }: { field: any; form: any }) => {
        const feedback = getIn(form.errors, field.name);
        const isTouched = getIn(form.touched, field.name);
        const isValid = isTouched && !getIn(form.errors, field.name);
        const isInvalid = !!feedback;

        const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
          field.onChange(event);
          // Force next tick to prevent validation
          // from running to soon
          setTimeout(() => {
            form.validateField(field.name);
            form.setFieldTouched(field.name);
          }, 0);
        };

        const classNames = {
          'is-invalid': isInvalid,
          'is-valid': isValid,
          'mr-3': !inline
        };

        return (
          <Form.Group {...bsProps}>
            {label && <Form.Label>{label}</Form.Label>}
            <InputGroup>
              {options.map((option) => {
                const isChecked = valueMatch(option?.value?.toString(), field?.value?.toString());

                return (
                  <React.Fragment key={`${option.label}-${option.value}`}>
                    <Form.Check
                      {...field}
                      isValid={isValid}
                      isInvalid={isInvalid}
                      type="radio"
                      id={`${field.name}-${option.value}`}
                      inline={inline}
                      custom={custom}
                      value={option.value}
                      label={option.label}
                      checked={isChecked}
                      className={classNames}
                      data-testid={`${field.name}-${option.value}`}
                      onChange={handleChange}
                    />
                  </React.Fragment>
                );
              })}
              <Form.Control.Feedback type="invalid">{feedback && t(feedback)}</Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        );
      }}
    </Field>
  );
};
